import * as RadixCollapsible from '@radix-ui/react-collapsible';

export type CollapsibleRootProps = RadixCollapsible.CollapsibleProps;
export type CollapsibleTriggerProps = RadixCollapsible.CollapsibleTriggerProps;
export type CollapsibleContentProps = RadixCollapsible.CollapsibleContentProps;

/**
 * An unstyled primitive for showing and hiding a section of content.
 *
 * @example Uncontrolled
 * ```tsx
 * <Collapsible.Root defaultOpen>
 * 	<Collapsible.Trigger>Details</Collapsible.Trigger>
 * 	<Collapsible.Content>Hidden until expanded</Collapsible.Content>
 * </Collapsible.Root>
 * ```
 *
 * @example Controlled
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <Collapsible.Root open={open} onOpenChange={setOpen}>
 * 	<Collapsible.Trigger>Details</Collapsible.Trigger>
 * 	<Collapsible.Content>Hidden until expanded</Collapsible.Content>
 * </Collapsible.Root>
 * ```
 */
export const Collapsible = {
	/**
	 * Wraps the trigger and the content, and owns the open state. Use `open` +
	 * `onOpenChange` for controlled usage, or `defaultOpen` for uncontrolled.
	 */
	Root: RadixCollapsible.Root,

	/**
	 * The button that toggles the content. Renders a `<button>` by default;
	 * pass `asChild` to render a single custom element instead.
	 */
	Trigger: RadixCollapsible.Trigger,

	/**
	 * The content that is shown while the collapsible is open.
	 */
	Content: RadixCollapsible.Content,
} as const;
